import VueCookies from 'vue-cookies';
// eslint-disable-next-line import/extensions
import http, { setAuthorizationHeader } from '@common/services/http';
import { stayUpdated } from '@common/services/dashboard';
import { changeUser } from '@common/services/users';
import { getUserNavigationOptions } from '@common/services/options';

// eslint-disable-next-line
const BFF_BASE_URL = process.env.BFF_BASE_URL;
// eslint-disable-next-line
const USERS_SLS_BASE_URL = process.env.USERS_SLS_BASE_URL;

async function getRequestBase(jwt, endpoint, params = {}, baseUrl) {
  try {
    if (jwt) {
      const response = await http({
        method: 'GET',
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
        url: `${baseUrl}${endpoint}`,
        params,
        validateStatus: () => true,
      });

      if (response.status === 401) {
        window.postMessage({ name: 'iConnectionsEventBus', type: 'logout' }, '*');
      }

      if (response.status === 200 && response && response.data && response.data.success) {
        return { status: true, data: response.data.data };
      }
      return { status: false, error_code: 'RESPONSE_NOT_SUCCESSFUL' };
    }
    return { status: false, error_code: 'INVALID_JWT' };
  } catch (e) {
    console.error(e);
    return { status: false, error_code: 'GENERIC_ERROR' };
  }
}

async function getRequest(jwt, endpoint, params = {}) {
  return getRequestBase(jwt, endpoint, params, BFF_BASE_URL);
}
async function getUserProfileV2(jwt, payload) {
  try {
    if (jwt) {
      const response = await http({
        method: 'GET',
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
        url: `${USERS_SLS_BASE_URL}/getUserProfile`,
        params: {
          contactId: payload.contactId,
          eventId: payload.eventId,
        },
        validateStatus: () => true,
      });
      if (response && response.status === 200 && response.data) {
        return { status: true, data: response.data };
      }
      return {
        status: false,
        error_code: 'RESPONSE_NOT_SUCCESSFUL',
        message: response.data.message,
      };
    }
    return { status: false, error_code: 'INVALID_JWT' };
  } catch (e) {
    return { status: false, error_code: 'GENERIC_ERROR' };
  }
}

async function getRequestUserSls(jwt, endpoint, params = {}) {
  try {
    if (jwt) {
      const response = await http({
        method: 'GET',
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
        url: `${USERS_SLS_BASE_URL}${endpoint}`,
        params,
        validateStatus: () => true,
      });

      if (response.status >= 200 && response.status < 300 && response && response.data) {
        return { status: true, data: response.data };
      }
      return { status: false, error_code: 'RESPONSE_NOT_SUCCESSFUL' };
    }
    return { status: false, error_code: 'INVALID_JWT' };
  } catch (e) {
    console.error(e);
    return { status: false, error_code: 'GENERIC_ERROR' };
  }
}

async function postRequestUserSls(jwt, endpoint, payload = {}) {
  try {
    if (jwt) {
      const response = await http({
        method: 'POST',
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
        url: `${USERS_SLS_BASE_URL}${endpoint}`,
        payload,
        validateStatus: () => true,
      });

      if (response.status === 401) {
        window.postMessage({ name: 'iConnectionsEventBus', type: 'logout' }, '*');
      }

      if (response.status === 200 && response && response.data && response.data.success) {
        return { status: true, data: response.data.data };
      }
      return { status: false, error_code: 'RESPONSE_NOT_SUCCESSFUL' };
    }
    return { status: false, error_code: 'INVALID_JWT' };
  } catch (e) {
    console.error(e);
    return { status: false, error_code: 'GENERIC_ERROR' };
  }
}

async function getFeatureFlags(identifier) {
  try {
    const response = await http({
      method: 'GET',
      url: `${process.env.FLAGSMITH_URL}identities/`,
      headers: {
        'x-environment-key': process.env.FLAGSMITH_API_KEY || '',
      },
      params: {
        identifier,
      },
      validateStatus: () => true,
    });

    if (response.status === 200 && response) {
      return { status: true, data: response.data };
    }
    return { status: false, error_code: 'RESPONSE_NOT_SUCCESSFUL' };
  } catch (e) {
    console.error(e);
    return { status: false, error_code: 'GENERIC_ERROR' };
  }
}

export default {
  namespaced: true,
  actions: {
    async setToken(context, payload) {
      try {
        const { token, refreshToken } = payload;
        context.commit('SET_TOKENS', { token, refreshToken });
        VueCookies.set('token', token, '6m', null, null, true, 'Strict');
        if (refreshToken) {
          VueCookies.set('refreshToken', refreshToken, '6m', null, null, true, 'Strict');
        }
        setAuthorizationHeader(token, refreshToken);
        const jwtData = JSON.parse(atob(token.split('.')[1]));
        context.commit('SET_JWT_DATA', jwtData);

        // Create legacy token and copilot token
        VueCookies.set('accessToken', token, '6m', null, '.iconnections.io', true, 'Strict');
        VueCookies.set(
          'user',
          {
            contactId: jwtData.ContactID,
            contactGuid: jwtData.ContactGUID,
            companyId: jwtData.CompanyID,
            companyTypeId: jwtData.CompanyTypeID,
            firstName: jwtData.FirstName,
            lastName: jwtData.LastName,
            initials: jwtData.navigationUserData.initials,
            email: jwtData.Email,
            imageURL: jwtData.navigationUserData.imageURL,
            adminCompany: jwtData.AdminCompany,
            adminLoggedInAs: jwtData.AdminLoggedInAs,
            clientId: jwtData.ClientID,
            subscriber: jwtData.Subscriber,
            skipTerms: jwtData.SkipTerms,
            whiteLabels: [],
            companyName: '', // Doesnt return on the current token,
            jobTitle: '', // Doesnt return on the current token,
            authenticationCode: '000000', // Doesnt return on the current token,
          },
          '6m',
          null,
          '.iconnections.io',
          true,
          'Strict',
        );
        VueCookies.set(
          'userCookie',
          {
            cssName: 'iConnectionsWhite',
            clientImageUrl:
              'https://iconnections.s3.us-east-2.amazonaws.com/clients/iconnections/iConnections-logo.svg',
            clientHexColor: '#692b7e',
            token,
            user: null,
            contactId: jwtData.ContactID,
            contactGuid: jwtData.ContactGUID,
            companyId: jwtData.CompanyID,
            companyName: '',
            companyTypeId: jwtData.CompanyTypeID,
            firstName: jwtData.FirstName,
            lastName: jwtData.LastName,
            initials: jwtData.navigationUserData.initials,
            email: jwtData.Email,
            authenticationCode: '000000',
            imageURL: jwtData.navigationUserData.imageURL,
            adminCompany: jwtData.AdminCompany,
            adminLoggedInAs: jwtData.AdminLoggedInAs,
            clientId: jwtData.ClientID,
            subscriber: jwtData.Subscriber,
            skipTerms: jwtData.SkipTerms,
          },
          '6m',
          null,
          '.iconnections.io',
          true,
          'Strict',
        );

        await context.dispatch('getUserInfo');
        await context.dispatch('getFeatureFlags');
        await context.dispatch('getNotificationsCount');
        if (jwtData?.CompanyTypeID === 2) {
          await context.dispatch('getUserProfile');
        }
      } catch (e) {
        // If there is an error, logout
        console.error(e);
        window.postMessage({ name: 'iConnectionsEventBus', type: 'logout' }, '*');
      }
    },

    deleteToken(context) {
      context.commit('CLEAR_ALL');
      VueCookies.remove('token');
      VueCookies.remove('token', '/', '.iconnections.io');
      VueCookies.remove('accessToken');
      VueCookies.remove('tokenIP');
      VueCookies.remove('refreshTokenIP');
      VueCookies.remove('user');
      VueCookies.remove('refreshToken');
      VueCookies.remove('userCookie');
      VueCookies.remove('adminToken');
    },

    async checkOnboardingRequired(context) {
      const { token, infoUser } = context.getters;
      const onboardingRequired = await getRequest(token, '/onboarding/required');
      if (onboardingRequired.status === true) {
        if (onboardingRequired.data && onboardingRequired.data.termsRequired) {
          // Force Terms of Service redirect
          window.postMessage({ name: 'iConnectionsEventBus', type: 'termsRequired' }, '*');
        } else if (
          onboardingRequired.data
          && onboardingRequired.data.aiqpRequired
          && infoUser?.subscriber
        ) {
          // Force AIQP redirect
          window.postMessage({ name: 'iConnectionsEventBus', type: 'aiqpRequired' }, '*');
          // only to subscriber, to that basic account not go to this route
        } else if (
          onboardingRequired.data
          && onboardingRequired.data.onboardingRequired
          && infoUser?.subscriber
        ) {
          // Force onboarding redirect
          window.postMessage({ name: 'iConnectionsEventBus', type: 'onboardingRequired' }, '*');
        }
      }
    },

    async getUserInfo(context) {
      const { token } = context.getters;
      if (token) {
        let navData = await getUserNavigationOptions();
        if (navData.status === false) {
          navData = await getRequest(token, '/navigation/options');
        }
        const jwtData = JSON.parse(atob(token.split('.')[1]));
        context.commit('SET_JWT_DATA', jwtData);
        if (navData.status === true) {
          context.commit('SET_NAV_DATA', navData.data);
        } else {
          // context.commit("REQUEST_FAILED_SET", data.error_code);
        }
      }
    },

    async getNotificationsCount(context) {
      const { token } = context.getters;
      const { featureFlags } = context.getters;

      if (!featureFlags?.main?.new_notifications_endpoint) {
        const notificationsCount = await getRequest(token, '/navigation/notification_counts');
        if (notificationsCount.status === true) {
          context.commit('SET_NOTIFICATION_DATA', notificationsCount.data);
        }
      } else if (token) {
        const notifications = await stayUpdated();
        const notificationsCount = {};

        notifications.forEach((notification) => {
          if (notification.type === 'pending_meetings') {
            notificationsCount.pendingMeetingsCount = notification?.count;
          } else if (notification.type === 'new_messages') {
            notificationsCount.unreadMessagesCount = notification?.count;
          } else if (notification.type === 'pending_connections_request') {
            notificationsCount.pendingRequestsCount = notification?.count;
          }
        });
        context.commit('SET_NOTIFICATION_DATA', notificationsCount);
        context.commit('SET_STAY_UPDATED_DATA', notifications);
      }
    },

    addModule(context, payload) {
      context.commit('ADD_MODULE', payload);
    },

    setCurrentExpectedPaths(context, payload) {
      context.commit('SET_CURRENT_EXPECTED_PATHS', payload || []);
    },

    termsRequired(context, payload) {
      context.commit('SET_TERMS_REQUIRED', payload);
    },
    async fetchTermsOfServiceContent(context) {
      const { token } = context.getters;
      const termsOfServiceContent = await getRequestUserSls(token, '/terms_of_service_content');
      context.commit('SET_TERMS_OF_SERVICE_CONTENT', termsOfServiceContent?.data?.content || null);
    },

    async getUserProfile(context) {
      const { token } = context.getters;
      const payload = await getUserProfileV2(token, { contactId: context.getters.contactId });
      context.commit('SET_USER_PROFILE', payload.data);
    },

    async acceptTermsOfService(context) {
      const { token } = context.getters;
      await postRequestUserSls(token, '/accept_terms_of_service');
      context.dispatch('checkOnboardingRequired');
      window.postMessage({ name: 'iConnectionsEventBus', type: 'termsAccepted' }, '*');
    },

    aiqpRequired(context, payload) {
      context.commit('SET_AIQP_REQUIRED', payload);
    },

    onboardingRequired(context, payload) {
      context.commit('SET_ONBOARDING_REQUIRED', payload);
    },

    async getFeatureFlags(context) {
      const { contactId } = context.getters;

      try {
        // Ping the endpoint
        const featureFlags = await getFeatureFlags(contactId || null);

        // Parse the response
        const parsedFlags = {};
        if (featureFlags.status === true && featureFlags.data) {
          // Allow parsing for both types of flags - with and without traits
          let flags = featureFlags.data;
          if (featureFlags.data.flags?.length > 0) {
            flags = featureFlags.data.flags;
          }

          flags.forEach((flag) => {
            if (flag && flag.feature && flag.feature.name) {
              const splitFlagName = flag.feature.name.split('/') || [];
              if (
                splitFlagName.length === 3
                && (splitFlagName[0] === 'web' || splitFlagName[0] === 'all')
              ) {
                const flagMFe = splitFlagName[1];
                const flagName = splitFlagName[2];

                if (!parsedFlags[flagMFe]) {
                  parsedFlags[flagMFe] = {};
                }
                parsedFlags[flagMFe][flagName] = flag.enabled || false;

                // If the flag is enabled, check if there is a payload
                if (flag.enabled && flag.feature_state_value) {
                  parsedFlags[flagMFe][`${flagName}_payload`] = JSON.parse(flag.feature_state_value);
                }
              }
            }
          });

          // Grabbing flags without a contactId does not utilize flagsmith segments
          // We should only consider the flags loaded if the user traits are loaded
          // otherwise the app will show the default flag settings for a second before loading traits
          if (contactId) {
            parsedFlags.isSegmentsLoaded = true;
          }

          parsedFlags.isLoaded = true;

          context.commit('UPDATE_FEATURE_FLAGS', parsedFlags);
        } else {
          const defaultFlags = {
            isLoaded: true,
            isSegmentsLoaded: true,
          };

          context.commit('UPDATE_FEATURE_FLAGS', defaultFlags);
        }
      } catch (e) {
        // if flag call fails set flags loaded
        const defaultFlags = {
          isLoaded: true,
          isSegmentsLoaded: true,
        };

        context.commit('UPDATE_FEATURE_FLAGS', defaultFlags);
      }
    },

    async getAliases(context) {
      const { token } = context.getters;
      const aliases = await getRequestUserSls(token, '/alias_accounts');
      if (aliases.status === true) {
        context.commit('SET_ACCOUNT_ALIASSS', aliases.data);
      }
    },

    async changeUser(context, payload) {
      const { contactGUID, companyGUID, email } = payload;

      // Retrieve a new JWT from the backend
      const { token, refreshToken } = await changeUser(contactGUID, companyGUID, email);
      // Clean up the existing cookies
      await context.dispatch('deleteToken');

      // Update to the new tokem
      await context.dispatch('setToken', { token, refreshToken });

      // Finally redirect to / to apply the new token
      window.postMessage({ name: 'iConnectionsEventBus', type: 'changeMFe', path: '/' }, '*');
    },
    async retrieveEventsRegistered(context) {
      const { token } = context.getters;
      const retrieveResponse = await getRequest(token, '/events');
      if (retrieveResponse.status) {
        const { registeredEvents } = retrieveResponse.data;
        return registeredEvents;
      }
      return null;
    },
  },
  mutations: {
    SET_TOKENS(state, payload) {
      state.token = payload.token;
      state.refreshToken = payload.refreshToken;
    },
    CLEAR_ALL(state) {
      state.token = null;
      state.refreshToken = null;
      state.name = '';
      state.initials = '';
      state.profileImg = null;
      state.mainOptions = [];
      state.myOptions = [];
      state.userOptions = [];
      state.unreadMessagesCount = false;
      state.pendingMeetingsCount = false;
      state.pendingRequestsCount = false;
      state.stayUpdatedData = [];
      state.companyId = null;
      state.companyTypeId = null;
      state.contactId = null;
      state.email = '';
      state.MFeModules = {};
      state.featureFlags = {};
    },
    SET_NAV_DATA(state, payload) {
      state.name = payload.user.name;
      state.contactId = payload.user.contactId;
      state.initials = payload.user.initials;
      state.profileImg = payload.user.profileImg;
      state.subscriber = payload.user.subscriber;
      state.mainOptions = payload.mainOptions;
      state.unreadMessagesCount = payload.unreadMessagesCount;
      state.pendingMeetingsCount = payload.pendingMeetingsCount;
      state.pendingRequestsCount = payload.pendingRequestsCount;
    },
    SET_NOTIFICATION_DATA(state, payload) {
      state.unreadMessagesCount = payload.unreadMessagesCount;
      state.pendingMeetingsCount = payload.pendingMeetingsCount;
      state.pendingRequestsCount = payload.pendingRequestsCount;
    },
    SET_STAY_UPDATED_DATA(state, payload) {
      state.stayUpdatedData = payload;
    },
    SET_ACCOUNT_ALIASSS(state, payload) {
      state.accountAliases = payload;
    },
    SET_JWT_DATA(state, payload) {
      state.companyId = payload?.CompanyID || payload?.CompanyId;
      state.companyTypeId = payload.CompanyTypeId;
      state.contactId = payload.contactId;
      state.contactGuid = payload.ContactGUID;
      state.email = payload.Email;
      state.CompanyTypeID = payload.CompanyTypeID;
      state.subscriber = typeof payload?.Subscriber === 'string' ? payload?.Subscriber === 'true' : payload?.Subscriber;
      state.isAdmin = payload.IsAdmin;
    },
    ADD_MODULE(state, payload) {
      state.MFeModules[payload.MFeName] = payload.MFeApp;
    },
    SET_CURRENT_EXPECTED_PATHS(state, payload) {
      state.currentExpectedPaths = payload;
    },
    UPDATE_FEATURE_FLAGS(state, payload) {
      state.featureFlags = payload;
    },
    SET_TERMS_REQUIRED(state, payload) {
      state.termsRequired = payload;
    },
    SET_TERMS_OF_SERVICE_CONTENT(state, payload) {
      state.termsOfServiceContent = payload;
    },
    SET_AIQP_REQUIRED(state, payload) {
      state.aiqpRequired = payload;
    },
    SET_ONBOARDING_REQUIRED(state, payload) {
      state.onboardingRequired = payload;
    },
    SET_USER_PROFILE(state, payload) {
      state.userProfile = payload;
    },
  },
  state: {
    token: null,
    refreshToken: null,

    // Navigation
    name: '',
    initials: '',
    profileImg: null,
    mainOptions: [],
    myOptions: [],
    userOptions: [],
    unreadMessagesCount: false,
    pendingMeetingsCount: false,
    pendingRequestsCount: false,
    stayUpdatedData: [],
    accountAliases: null,

    // JWT
    companyId: null,
    companyTypeId: null,
    contactId: null,
    contactGuid: null,
    email: '',
    CompanyTypeID: '',
    subscriber: '',
    isAdmin: false,

    // Micro Frontend Modules
    MFeModules: {},

    // Expected paths per MFe
    currentExpectedPaths: {},

    // Feature Flags
    featureFlags: {
      isSegmentsLoaded: false,
      isLoaded: false,
    },

    termsRequired: false,
    termsOfServiceContent: null,
    aiqpRequired: false,
    onboardingRequired: false,
    userProfile: null,
  },
  getters: {
    token(state) {
      return state.token;
    },
    refreshToken(state) {
      return state.refreshToken;
    },
    accountAliases: (state) => state.accountAliases,
    navInfo(state) {
      return {
        name: state.name,
        contactId: state.contactId,
        initials: state.initials,
        profileImg: state.profileImg,
        subscriber: state.subscriber,
        mainOptions: state.mainOptions,
      };
    },
    notificationsCount(state) {
      return {
        unreadMessagesCount: state.unreadMessagesCount,
        pendingMeetingsCount: state.pendingMeetingsCount,
        pendingRequestsCount: state.pendingRequestsCount,
      };
    },
    stayUpdatedData(state) {
      return state.stayUpdatedData;
    },
    MFeModules(state) {
      return state.MFeModules;
    },
    MFeModulesLength(state) {
      return Object.keys(state.MFeModules).length;
    },
    currentExpectedPaths(state) {
      return state.currentExpectedPaths;
    },
    featureFlags(state) {
      return state.featureFlags;
    },
    termsRequired: (state) => state.termsRequired,
    termsOfServiceContent: (state) => state.termsOfServiceContent,
    onboardingRequired(state) {
      return state.onboardingRequired;
    },
    contactId: (state) => state.contactId,
    contactGuid: (state) => state.contactGuid,
    isAdmin: (state) => state.isAdmin,
    companyTypeId: (state) => state.CompanyTypeID,
    infoUser(state) {
      return {
        contactId: state.contactId,
        email: state.email,
        CompanyTypeID: state.CompanyTypeID,
        subscriber: state.subscriber,
        companyId: state.companyId,
      };
    },
    subscriber: (state) => state.subscriber,
    userProfile: (state) => state.userProfile,
  },
};
